import { QConfigStatusTag } from '@exam-management/components';
import { QConfigType, QConfigStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { DeleteQConfigModal } from '@exam-management/qconfig-detail';
import { QConfigDuplicationModal } from '@exam-management/qconfig-duplication';
import {
  ContentCopyRoundedIcon,
  EllipsisText,
  MoreMenuIconButton,
  type GridColDef,
  type MoreMenuIconButtonOption,
  Text,
  DeleteOutlineIcon,
  theme,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';
import { formatDateTime } from '@shared/utils';
import { useMemo } from 'react';

export const QCONFIG_TYPE_TEXT = {
  [QConfigType.Manual]: 'Manual',
  [QConfigType.Optimized]: 'Optimized',
  [QConfigType.ManualOptimized]: 'Manual/Optimized',
};

export interface QConfig {
  id: string;
  code: number;
  name: string;
  type: QConfigType;
  status: QConfigStatus;
  updatedAt: Date;
}

export const columns: GridColDef<QConfig>[] = [
  {
    field: 'code',
    headerName: 'ID',
    width: 120,
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => {
      return <Text variant='body2'>{params.row.code}</Text>;
    },
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      return (
        <EllipsisText maxLines={2} variant='body2Semibold'>
          {params.row.name}
        </EllipsisText>
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    width: 140,
    renderCell: (params) => {
      return <QConfigStatusTag status={params.row.status} />;
    },
  },
  {
    field: 'updatedAt',
    headerName: 'Last Modified',
    width: 160,
    renderCell: (params) => {
      return <Text variant='body2'>{formatDateTime(params.row.updatedAt)}</Text>;
    },
  },
  {
    field: 'moreAction',
    headerName: '',
    width: 48,
    sortable: false,
    renderCell: (params) => {
      const [isDuplicationModalOpen, { open: openDuplicationModal, close: closeDuplicationModal }] =
        useDisclosure(false);

      const [
        isDeleteConfirmationModalOpen,
        { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
      ] = useDisclosure(false);

      // const handleEditQConfig = () => {};
      const handleDuplicateQConfig = () => {
        openDuplicationModal();
      };
      const hasDuplicatePermission = useHasPermission(APP_PERMISSIONS.EXAM.QCONFIG.CREATE);

      const hasDeletePermission = useHasPermission(APP_PERMISSIONS.EXAM.QCONFIG.DELETE);

      const moreActionOptions = useMemo(() => {
        const options: MoreMenuIconButtonOption[] = [];
        const duplicateOption = {
          icon: <ContentCopyRoundedIcon />,
          label: 'Duplicate',
          onClick: handleDuplicateQConfig,
        };

        const deleteOption = {
          icon: <DeleteOutlineIcon sx={{ color: (theme) => theme.palette.error.main }} />,
          label: 'Delete',
          onClick: openDeleteConfirmationModal,
          sx: {
            color: theme.palette.error.main,
          },
        };

        if (hasDuplicatePermission) {
          options.push(duplicateOption);
        }
        if (hasDeletePermission && params.row.status === QConfigStatus.Draft) {
          options.push(deleteOption);
        }

        return options;
      }, [handleDuplicateQConfig, hasDuplicatePermission, hasDeletePermission]);

      return (
        <>
          <MoreMenuIconButton options={moreActionOptions} />
          {isDuplicationModalOpen ? (
            <QConfigDuplicationModal
              onClose={closeDuplicationModal}
              open={isDuplicationModalOpen}
              qConfigId={params.row.id}
              name={params.row.name}
            />
          ) : null}
          <DeleteQConfigModal
            open={isDeleteConfirmationModalOpen}
            onClose={closeDeleteConfirmationModal}
            qconfigId={params.row.id}
          />
        </>
      );
    },
  },
];
